import type { LogAttributes } from 'posthog-js'
import posthog from 'posthog-js'

import { isPosthogInitialized } from './posthog'
import type { MonitoringUserInfo } from './user-info'
import { getMonitoringUserInfo } from './user-info'

export const LOGGER_EVENTS = {
  APOLLO_CLIENT: 'APOLLO_CLIENT',
  AUTH: 'AUTH',
  AI_CHAT: 'AI_CHAT',
  FILTER_BAR: 'FILTER_BAR',
} as const
export type LoggerEvent = keyof typeof LOGGER_EVENTS

type ConsoleLevel = 'info' | 'warn' | 'error'

function composeMessage(eventName: LoggerEvent, message: string, user: MonitoringUserInfo | undefined): string {
  return `[${eventName}] ${user?.name ?? '(unknown user)'} ${user?.tenant.name ?? ''} ${message}`
}

// このファイルはログ出力そのものが目的のフォールバック経路のため、
// no-console（oxlint, error）の対象から .oxlintrc.json の override で明示的に除外している
function writeConsole(level: ConsoleLevel, message: string, attributes?: LogAttributes): void {
  console[level](message, attributes)
}

// フロントエンドの外部ログ基盤は logtail から PostHog Logs へ移行した。
// 閲覧実績の無かった info/debug（1リクエストごとのAPI疎通ログ等）まで本番へ送ると
// PostHog 側の書き込み量が過大になるため、本番送信は warn/error のみに絞り、
// info は開発時のコンソール出力のみに留める（本番では no-op）。
async function send(
  level: 'warn' | 'error',
  eventName: LoggerEvent,
  message: string,
  body?: LogAttributes,
): Promise<void> {
  const user = getMonitoringUserInfo()
  const composedMessage = composeMessage(eventName, message, user)
  const attributes: LogAttributes = { ...body, eventName, user }
  if (isPosthogInitialized) {
    posthog.logger[level](composedMessage, attributes)
    return
  }
  // PostHog 未初期化（トークン未設定の開発環境等）はコンソールへフォールバックする
  writeConsole(level, composedMessage, attributes)
}

export const logger = {
  async info(eventName: LoggerEvent, message: string, body?: LogAttributes): Promise<void> {
    if (import.meta.env.DEV) {
      writeConsole('info', composeMessage(eventName, message, getMonitoringUserInfo()), body)
    }
  },
  async warn(eventName: LoggerEvent, message: string, body?: LogAttributes): Promise<void> {
    await send('warn', eventName, message, body)
  },
  async error(eventName: LoggerEvent, message: string, body?: LogAttributes): Promise<void> {
    await send('error', eventName, message, body)
  },
}
